import { ApplicationRef, Component, ElementRef, Input, Renderer2, AfterViewInit } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Client } from 'src/providers/client/client';
import * as confetti from 'canvas-confetti';
import { spookyConfetti } from 'src/shared/util/halloween';
import { ModalController } from '@ionic/angular';
import { debounceTime } from 'rxjs/operators';

import { ReportIssue } from 'src/providers/report-issue/report-issue';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import { Util } from 'src/shared/util/util';
import { snakeCaseToTitleCase } from 'src/shared/util/snake-to-tile-case';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
    selector: 'task-card',
    templateUrl: 'task-card.html',
    styleUrls: ['./task-card.scss'],
    standalone: false
})
export class TaskCardComponent implements AfterViewInit {

  @Input() issue: any;
  @Input() addressResponse: any;
  @Input() issueReportTypes: any;
  issueForm: UntypedFormGroup;
  rootRef: Promise<ElementRef>;
  errorMessage: string;
  submitted = false;
  reportPhotos = [];
  urgencyOptions = [{viewValue: new TranslationPipe().transform('Low'), value: 'low'}, {viewValue: new TranslationPipe().transform('Normal'), value: 'normal'}, {viewValue: new TranslationPipe().transform('Emergency'), value: 'emergency'}];
  statusOptions = [{viewValue: new TranslationPipe().transform('Reported'), value: 'reported'}, {viewValue: new TranslationPipe().transform('Approved'), value: 'approved'}, {viewValue: new TranslationPipe().transform('In Progress'), value: 'in_progress'}, {viewValue: new TranslationPipe().transform('Done'), value: 'done'}];
  issueTypeOptions;
  propertyOptions;
  
  
  boundSaveIssue = this.saveIssue.bind(this);

constructor(
    private fb: FormBuilder,
    private client: Client,
    private renderer2: Renderer2,
    private elementRef: ElementRef,
    aplicationRef: ApplicationRef,
    private modalCtrl: ModalController,
    private reportIssue: ReportIssue,
    private util: Util
  ) {
    this.rootRef = new Promise(resolve => {
      setTimeout(
        () => resolve(aplicationRef.components[0].injector.get(ElementRef)),
        100
      );
    });
  }

  ngAfterViewInit() {
    this.issueTypeOptions = this.issueReportTypes.map(issueType => ({viewValue: new TranslationPipe().transform(snakeCaseToTitleCase(issueType.name)), value: issueType.id}));
    const otherOption = this.issueTypeOptions.find(option => option.viewValue === 'Other');
    this.propertyOptions = this.client.parseAddressList(this.addressResponse, false);
    this.issueForm = this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      urgency: ['normal', Validators.required],
      address_id: ['', Validators.required],
      status: ['reported', Validators.required],
      issue_report_type_id: [otherOption.value, Validators.required],
    });
    if (this.issue) {
      this.patchIssueForm();
      this.subscribeToIssueChanges();
    }
  }

  patchIssueForm() {
    this.issueForm.patchValue({
      title: this.issue.title,
      description: this.issue.description,
      urgency: this.issue.urgency,
      address_id: this.issue.address_id,
      status: this.issue.status,
      issue_report_type_id: this.issue.issue_report_type.id,
    });
    this.reportPhotos = this.issue.issue_report_photos;
  }

  subscribeToIssueChanges() {
    for (const field in this.issueForm.controls) {
      const control = this.issueForm.get(field);
      control.valueChanges.pipe(
        debounceTime(field === 'description' ? 300 : 0)
      ).subscribe(value => {
        this.updateIssue(field, value);
      });
    }
  }

  get issueHeaderIcon(): string {
    const icons = {
      'low': 'assets/img/low_priority.svg',
      'normal': 'assets/img/normal_priority.svg',
      'high': 'assets/img/high_priority.svg',
      'emergency': 'assets/img/emergency_priority.svg'
    };
    return icons[this.issue?.urgency] || icons['normal'];
  }

  async updateIssue(field, value) {
    this.submitted = true;
    try {
      const params = {[field]: value};
      await this.reportIssue.updateIssueReport(this.issue.id, params);
      if(field === 'status' && value === 'done') {
        this.throwConfetti();
      }
      this.issue[field] = value;
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }
 
  async throwConfetti() {
    const canvas = this.renderer2.createElement('canvas');
    canvas.addEventListener('click', () => canvas.style.display = 'none');
    this.renderer2.appendChild((await this.rootRef).nativeElement, canvas);
    const confettiAnimation = confetti.create(canvas, { resize: true });
    await confettiAnimation(spookyConfetti({
      particleCount: 25,
      origin: { y: 0.7 },
    }))
    this.renderer2.removeChild((await this.rootRef).nativeElement, canvas);
  }

  async deleteIssue(issue) {
    const params: ConfirmPageParamsModel = {
      title: 'Delete Issue?',
      body: 'You cannot undo this action.',
      backText: 'Go Back',
      confirmText: 'Delete ',
      confirmAction: this.confirmDeleteIssue.bind(this),
      confirmActionParams: issue
    }
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    confirmationModal.onDidDismiss().then(() => this.modalCtrl.dismiss());
    await confirmationModal.present();
  }

  async confirmDeleteIssue(issue) {
    try {
      await this.reportIssue.deleteReportedIssue(issue?.id);
      this.modalCtrl.dismiss();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  async addAttachment() {
    const attachment = await this.reportIssue.addAttachment();
    const newAttachmentIndex = this.reportPhotos.length;
    if (attachment.filename) {
      this.reportPhotos.push({
        description: attachment.filename,
        photo_url: attachment.fileKey
      });
    }
    if (this.issue) {
      this.reportIssue.saveAttachment(this.issue.id, attachment).then((newAttachment) => {
        this.reportPhotos[newAttachmentIndex] = newAttachment;        
      }).catch((error) => {
        this.reportPhotos.splice(newAttachmentIndex, 1);
        this.errorMessage = (error.error && error.error.message) ? error.error.message : error.message;
      });
    }
  }

  async removeAttachment(index, attachment) {
    try {
      if (this.issue) {
        await this.reportIssue.deleteAttachment(attachment.id);
      }
      this.reportPhotos.splice(index, 1);
    } catch (error) {
      this.errorMessage = (error.error && error.error.message) ? error.error.message : error.message;
    }
  }

  async saveIssue() {
    this.submitted = true;
    if (this.issueForm.invalid) {return;}
    const data = this.issueForm.value;
    try {
      data['issue_report_photos'] = this.reportPhotos;
      this.issue = await this.reportIssue.reportIssue(data);
      this.patchIssueForm();
      this.subscribeToIssueChanges();
      this.modalCtrl.dismiss();
    } catch (error) {
      this.errorMessage = (error.error && error.error.message) ? error.error.message : error.message;
    }
  }

  close() {
    this.modalCtrl.dismiss();
  }

  openAttachment(url) {
    this.util.openUrl(url);
  }
}
